import { Injectable } from '@angular/core';
import { tap, withLatestFrom } from 'rxjs/operators';
import * as AnalyticsActions from './analytics.actions';
import { getAnalytics, logEvent } from 'firebase/analytics';
import packageJson from 'package.json';
import { selectCurrentLang, selectIsDraftMode, selectIsImpersMode, selectUser } from '../user/user.selector';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppState } from '../app.reducer';
import { TranslateService } from '@ngx-translate/core';
import { WebsocketThermoService } from 'src/app/services/websocket-thermo.service';
import { Store } from '@ngrx/store';
import { selectCurrentDeviceInfo, selectCurrentDeviceModel, selectCurrentDeviceStatus } from '../device/device.selector';

@Injectable()
export class AnalyticsEffects {
   eventCustomPageView$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventCustomPageView),
      withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang), this.store.select(selectIsImpersMode), this.store.select(selectIsDraftMode)),
      tap(([action, currentUser, currentLang, isImpersMode, isDraftMode]) => {
         const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
         const langCode = currentLang ? currentLang.code : 'no_data';
         const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
         let eventParams = {};
         if (isImpersMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'true', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (isDraftMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'true', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'TECHNICIAN') {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'USER') {
            eventParams = {
               ...{ user_role: 'USER', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'MARKETING') {
            eventParams = {
               ...{ user_role: 'MARKETING', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else {
            eventParams = {
               ...{ user_role: 'no_data', impers: 'no_data', draft: 'no_data', lang: langCode, nation: 'no_data', app_version: packageJson.version },
               ...action.params
            };
         }
         logEvent(getAnalytics(), 'custom_page_view', eventParams);
      }),
   ), { dispatch: false });

   eventNoAuth$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventNoAuth),
      withLatestFrom(this.store.select(selectCurrentLang)),
      tap(([action, currentLang]) => {
         const langCode = currentLang ? currentLang.code : 'no_data';
         let eventParams = {};
         eventParams = {
            ...{ user_role: 'guest', impers: 'guest', draft: 'guest', lang: langCode, nation: 'guest', app_version: packageJson.version },
            ...action.params
         };
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   eventNoAuthLangParam$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventNoAuthLangParam),
      tap((action) => {
         let eventParams = {};
         eventParams = {
            ...{ user_role: 'guest', impers: 'guest', draft: 'guest', nation: 'guest', app_version: packageJson.version },
            ...action.params
         };
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   eventLogin$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventLogin),
      withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang), this.store.select(selectIsImpersMode), this.store.select(selectIsDraftMode)),
      tap(([action, currentUser, currentLang, isImpersMode, isDraftMode]) => {
         const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
         const langCode = currentLang ? currentLang.code : 'no_data';
         const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
         let eventParams = {};
         if (isImpersMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'true', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (isDraftMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'true', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'TECHNICIAN') {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SERVICE') {
            // nation is set to db and could be empty
            eventParams = {
               ...{ user_role: 'SERVICE', impers: 'false', draft: 'false', lang: langCode, nation: 'service_user', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SUBSIDIARY') {
            // nation is set to db and could be empty
            eventParams = {
               ...{ user_role: 'SUBSIDIARY', impers: 'false', draft: 'false', lang: langCode, nation: 'subsidiary_user', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'USER') {
            eventParams = {
               ...{ user_role: 'USER', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else {
            eventParams = {
               ...{ user_role: 'no_data', impers: 'no_data', draft: 'no_data', lang: langCode, nation: 'no_data', app_version: packageJson.version },
               ...action.params
            };
         }
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   // ho dovuto duplicarlo rispetto a eventLogin perchè la flag nel reducer non mi faceva cathare login
   // no device event, no device params required
   eventFirstDateLogin$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventFirstDateLogin),
      withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang), this.store.select(selectIsImpersMode), this.store.select(selectIsDraftMode)),
      tap(([action, currentUser, currentLang, isImpersMode, isDraftMode]) => {
         const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
         const langCode = currentLang ? currentLang.code : 'no_data';
         const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
         let eventParams = {};
         if (isImpersMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'true', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (isDraftMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'true', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'TECHNICIAN') {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SERVICE') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SERVICE', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SUBSIDIARY') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SUBSIDIARY', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'USER') {
            eventParams = {
               ...{ user_role: 'USER', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else {
            eventParams = {
               ...{ user_role: 'no_data', impers: 'no_data', draft: 'no_data', lang: langCode, nation: 'no_data', app_version: packageJson.version },
               ...action.params
            };
         }
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   // ho dovuto duplicarlo rispetto a eventLogin perchè la flag nel reducer non mi faceva cathare login
   eventNewAction$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventNewAction),
      withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang), this.store.select(selectIsImpersMode), this.store.select(selectIsDraftMode),
      ),
      tap(([action, currentUser, currentLang, isImpersMode, isDraftMode]) => {
         const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
         const langCode = currentLang ? currentLang.code : 'no_data';
         const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
         let eventParams = {};
         if (isImpersMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'true', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (isDraftMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'true', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'TECHNICIAN') {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SERVICE') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SERVICE', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'SUBSIDIARY') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SUBSIDIARY', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...action.params
            };
         } else if (role === 'USER') {
            eventParams = {
               ...{ user_role: 'USER', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...action.params
            };
         } else {
            eventParams = {
               ...{ user_role: 'no_data', impers: 'no_data', draft: 'no_data', lang: langCode, nation: 'no_data', app_version: packageJson.version },
               ...action.params
            };
         }
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   // ho dovuto duplicarlo rispetto a eventLogin perchè la flag nel reducer non mi faceva cathare login
   eventDevice$ = createEffect(() => this.actions$.pipe(
      ofType(AnalyticsActions.eventDevice),
      withLatestFrom(this.store.select(selectUser), this.store.select(selectCurrentLang), this.store.select(selectIsImpersMode), this.store.select(selectIsDraftMode),
      this.store.select(selectCurrentDeviceModel), this.store.select(selectCurrentDeviceInfo), this.store.select(selectCurrentDeviceStatus)),
      tap(([action, currentUser, currentLang, isImpersMode, isDraftMode, deviceModel, deviceInfo, deviceStatus]) => {
         const productName = deviceModel && deviceModel.ProductName ? deviceModel.ProductName : '';
         const productBoardName = deviceModel && deviceModel.ProductBoardName ? deviceModel.ProductBoardName : '';
         const modelId = deviceInfo && deviceInfo.ModelId ? deviceInfo.ModelId : '';
         const fwVersion = deviceInfo && deviceInfo.FwVersion ? deviceInfo.FwVersion : '';
         const hwVersion = deviceInfo && deviceInfo.HwVersion ? deviceInfo.HwVersion : '';
         const online = deviceStatus && deviceStatus.Online ? deviceStatus.Online : false;
         const errored = deviceStatus && deviceStatus.Errored ? deviceStatus.Errored : false;
         const errorDescription = deviceStatus && deviceStatus.ErrorDescription ? deviceStatus.ErrorDescription : '';
         const limbo = deviceStatus && deviceStatus.Limbo ? deviceStatus.Limbo : false;
         const deviceParam = { productName: productName, productBoardName: productBoardName, modelId: modelId, fwVersion: fwVersion, hwVersion: hwVersion, deviceOnline: online,
            deviceErrored: errored, devErrorDescription: errorDescription, deviceLimbo: limbo
         };

         const role = currentUser && currentUser.Roles?.length > 0 ? currentUser.Roles[0].Description : 'no_data';
         const langCode = currentLang ? currentLang.code : 'no_data';
         const nation = currentUser ? currentUser.CountryName.toUpperCase() : 'no_data';
         let eventParams = {};
         if (isImpersMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'true', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else if (isDraftMode) {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'true', lang: langCode, nation: nation, app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else if (role === 'TECHNICIAN') {
            eventParams = {
               ...{ user_role: 'TECHNICIAN', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else if (role === 'SERVICE') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SERVICE', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else if (role === 'SUBSIDIARY') {
            // nation is set to db and could be empty so -> no available
            eventParams = {
               ...{ user_role: 'SUBSIDIARY', impers: 'false', draft: 'false', lang: langCode, nation: 'not_available', app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else if (role === 'USER') {
            eventParams = {
               ...{ user_role: 'USER', impers: 'false', draft: 'false', lang: langCode, nation: nation, app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         } else {
            eventParams = {
               ...{ user_role: 'no_data', impers: 'no_data', draft: 'no_data', lang: langCode, nation: 'no_data', app_version: packageJson.version },
               ...deviceParam,
               ...action.params
            };
         }
         logEvent(getAnalytics(), action.eventName, eventParams);
      }),
   ), { dispatch: false });

   constructor(
           private actions$: Actions,
           private store: Store<AppState>,
           private translate: TranslateService,
           private websocketThermoService: WebsocketThermoService,
         //   public app: AppComponent,
       ) { }
}